import { Link } from "@mui/material";

import { routes } from "@/global/routes";
import { vendorUUIDs } from "../../constants";
import { howItWorks, faqs } from "./helpers";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";

import SeatMap3dPreview from "public/images/devils/devils_flex_3d_venue.png";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

const logoImage =
  "https://ss-leagues.s3.amazonaws.com/NHL/NewJerseyDevils-aug21.png";
const findGamesBannerImage =
  "https://ss-leagues.s3.amazonaws.com/NHL/devils/devils-banner.jpg";
const backgroundImage =
  "https://ss-leagues.s3.amazonaws.com/NHL/devils/devils-background.jpeg";

const config: TeamConfig = {
  teamSchema: "devils_holiday",
  browserTitle: "Devils Holiday Packs",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "(973) 757-6200",
  teamName: "New Jersey Devils",

  manageCard: true,

  buckets: {
    enabled: true,
    prioritizeMinGameSelectionCount: true,
    list: [
      {
        id: 1,
        min: 1,
        max: 1,
        name: "Tier 1",
        description: "Select one game!",
        isPrimary: true,
        closeWhenHitMax: true,
        dontCloseWhenHitMin: true,
      },
      {
        id: 2,
        min: 4,
        max: 4,
        name: "Tier 2",
        description: "Select 4 games!",
        closeWhenHitMax: true,
        dontCloseWhenHitMin: true,
      },
    ],
  },

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-10215-devils-sec",
    mapId: "main",
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.purchaseSeats,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 5,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: true,
    checkoutTimerEnabled: false,
    showFrontendTotalAmount: false,
    showFrontendServiceFee: false,

    hideViewTicketsInThankYouPage: false,
    defaultSeatCount: 2,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
    checkoutTimerEnabled: false,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: 3,
    allowedPaymentFailCount: 3,
  },

  isSaveCardAllowed: true,

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "G-XT9NZ1847J",
    attrkPixelId: "",
    beaconId: "",
    metaPixelId: "120085088485875",
    stackAdaptPixelId: "pmYksymwpGkOLia4azzaJQ",
  },

  // call refresh inventory when selecting games
  refreshInventoryWhenEventClick: false,

  shouldCheckZipCode: false,

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/devils/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  // images
  images: {
    logoNameImage: "",
    findGamesBannerImage,
    faviconImage: logoImage,
    authBackgroundImage: backgroundImage,
    checkoutStatusPageImage: backgroundImage,
    seatMap3dPreviewImage: SeatMap3dPreview.src,
    logoImage: logoImage + "?width=64&height=64",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: false,
    },
    bottomBar: {
      showGameSelectionProgress: true,
    },

    hideCheckoutCartBalance: true,

    customAdaLabel: (
      <>
        To purchase accessible seating locations, please contact the Box Office
        at{" "}
        <Link
          href="tel:9737576600"
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          (973) 757-6600
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#CE1126",
  },

  howItWorks,
  faqs,

  languages: {
    topNavMenuItems: "",
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: {
      progressBarAditionalText: " to continue!",
      progressBarSuccessText:
        "You're good to go! Add more games or select Continue to view seating options.",
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Thank you for your New Jersey Devils Holiday Pack purchase!",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },
    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
